import { useMemo, useState } from "react";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@diametral/design-system/react";

import { usePeopleSearch } from "../api/people";
import type { PersonBrief, PickerPerson } from "../api/types";
import { useTranslation } from "../lib/i18n";
import { useDebounced } from "../lib/useDebounced";
import { RemovableTag } from "./RemovableTag";

interface SearchProps {
  /** Accessible name of the search input. */
  label: string;
  id?: string;
  /** Only people the DAR rule lets be assigned (direct assignment). */
  assignable?: boolean;
  placeholder?: string;
  excludeIds?: number[];
  disabled?: boolean;
}

function byName(a: PickerPerson, b: PickerPerson) {
  return a.name.localeCompare(b.name, "fr");
}

function usePeopleItems(query: string, assignable: boolean, excludeIds: number[] | undefined) {
  const debounced = useDebounced(query.trim(), 250);
  const search = usePeopleSearch(debounced, assignable);
  const items = useMemo(
    () => (search.data ?? []).filter((p) => !excludeIds?.includes(p.id)).sort(byName),
    [search.data, excludeIds],
  );
  return { items, search };
}

function PersonOption({ person }: { person: PickerPerson }) {
  const { t } = useTranslation();
  return (
    <span className="ada-person">
      <span className="ada-person__name">{person.name}</span>
      <span className="ada-person__title">
        {[person.title, person.email].filter(Boolean).join(" · ")}
        {person.assignable ? "" : ` · ${t("not assignable")}`}
      </span>
    </span>
  );
}

/** One person, searched on /api/people as you type (Base UI combobox, server-side filtering). */
export function PersonSelect({
  value,
  onChange,
  label,
  id,
  assignable = false,
  placeholder,
  excludeIds,
  disabled,
}: SearchProps & { value: PickerPerson | null; onChange: (person: PickerPerson | null) => void }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState(value?.name ?? "");
  const { items, search } = usePeopleItems(query, assignable, excludeIds);
  return (
    <Combobox
      items={items}
      filter={null}
      value={value}
      onValueChange={(next: PickerPerson | null) => onChange(next)}
      inputValue={query}
      onInputValueChange={(next: string) => setQuery(next)}
      itemToStringLabel={(person: PickerPerson) => person.name}
      isItemEqualToValue={(a: PickerPerson, b: PickerPerson) => a.id === b.id}
      disabled={disabled}
    >
      <ComboboxInput
        id={id}
        aria-label={label}
        placeholder={placeholder ?? t("Search for a person…")}
        showClear
        disabled={disabled}
      />
      <ComboboxContent>
        <ComboboxEmpty>{search.isFetching ? t("Searching…") : t("No person found")}</ComboboxEmpty>
        <ComboboxList>
          {(person: PickerPerson) => (
            <ComboboxItem key={person.id} value={person}>
              <PersonOption person={person} />
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

/** A search box that adds the picked person to a list, then clears itself. */
function PersonAdder({
  onAdd,
  label,
  id,
  assignable = false,
  placeholder,
  excludeIds,
  disabled,
}: SearchProps & { onAdd: (person: PickerPerson) => void }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const { items, search } = usePeopleItems(query, assignable, excludeIds);
  return (
    <Combobox
      items={items}
      filter={null}
      value={null}
      onValueChange={(next: PickerPerson | null) => {
        if (next) {
          onAdd(next);
          setQuery("");
        }
      }}
      inputValue={query}
      onInputValueChange={(next: string, details: { reason: string }) => {
        // Picking an item would fill the input with the name: the list owns it now.
        if (details.reason === "item-press") return;
        setQuery(next);
      }}
      itemToStringLabel={(person: PickerPerson) => person.name}
      isItemEqualToValue={(a: PickerPerson, b: PickerPerson) => a.id === b.id}
      disabled={disabled}
    >
      <ComboboxInput id={id} aria-label={label} placeholder={placeholder ?? t("Add a person…")} disabled={disabled} />
      <ComboboxContent>
        <ComboboxEmpty>{search.isFetching ? t("Searching…") : t("No person found")}</ComboboxEmpty>
        <ComboboxList>
          {(person: PickerPerson) => (
            <ComboboxItem key={person.id} value={person}>
              <PersonOption person={person} />
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

/** A list of people (TL, PO…): removable tags plus a search box to add one. */
export function PeopleListField({
  value,
  onChange,
  label,
  id,
  disabled,
}: {
  value: PersonBrief[];
  onChange: (people: PersonBrief[]) => void;
  label: string;
  id?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const ids = value.map((p) => p.id);
  return (
    <div className="ada-stack ada-stack--tight">
      {value.length ? (
        <div className="ada-cluster" aria-label={label}>
          {value.map((person) => (
            <RemovableTag
              key={person.id}
              disabled={disabled}
              removeLabel={t("Remove {{name}}", { name: person.name })}
              onRemove={() => onChange(value.filter((p) => p.id !== person.id))}
            >
              {person.name}
            </RemovableTag>
          ))}
        </div>
      ) : (
        <span className="ada-faint">{t("Nobody yet.")}</span>
      )}
      <PersonAdder
        id={id}
        label={t("{{label}}: add a person", { label })}
        excludeIds={ids}
        disabled={disabled}
        onAdd={(person) =>
          onChange([...value, { id: person.id, name: person.name, title: person.title, email: person.email }])
        }
      />
    </div>
  );
}
